// Visible indicator that one or more filters are active. Each filter
// renders as a chip with a remove (×) button. A "Clear all" link
// appears when more than one filter is active.

import { Filter, X } from 'lucide-react'
import type { ColumnDef } from '../types'
import type { FilterSpec } from './gridTypes'
import { listHeadingOf } from '../utils'

interface Props {
  filters: FilterSpec[]
  columns: ColumnDef[]            // for resolving column → heading
  columnKeyOf: (c: ColumnDef) => string
  onRemove: (column: string) => void
  onClearAll: () => void
}

export function columnHeading(columns: ColumnDef[], columnKeyOf: (c: ColumnDef) => string, key: string): string {
  for (const c of columns) {
    if (columnKeyOf(c) === key) return listHeadingOf(c) || key
  }
  return key
}

export function describeFilter(f: FilterSpec): string {
  switch (f.op) {
    case 'contains': return `contains "${f.value ?? ''}"`
    case 'equals': return `= ${formatVal(f.value)}`
    case 'not_equals': return `≠ ${formatVal(f.value)}`
    case 'starts_with': return `starts with "${f.value ?? ''}"`
    case 'ends_with': return `ends with "${f.value ?? ''}"`
    case 'gt': return `> ${formatVal(f.value)}`
    case 'gte': return `≥ ${formatVal(f.value)}`
    case 'lt': return `< ${formatVal(f.value)}`
    case 'lte': return `≤ ${formatVal(f.value)}`
    case 'between': return `${formatVal(f.from)} … ${formatVal(f.to)}`
    case 'is_empty': return 'is empty'
    case 'is_not_empty': return 'is not empty'
    case 'in':
      if (Array.isArray(f.value)) {
        return `in (${(f.value as unknown[]).map(formatVal).join(', ')})`
      }
      return 'in ()'
    default: return f.op
  }
}

function formatVal(v: unknown): string {
  if (v == null || v === '') return '∅'
  return String(v)
}

export default function FilterChipBar({
  filters, columns, columnKeyOf, onRemove, onClearAll,
}: Props) {
  if (!filters || filters.length === 0) return null
  return (
    <div
      title="These filters only narrow the rows already loaded — they don't change which records are fetched"
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
        padding: '6px 10px',
        background: 'rgba(234, 179, 8, 0.10)',
        borderTop: '1px solid rgba(234, 179, 8, 0.35)',
        borderBottom: '1px solid rgba(234, 179, 8, 0.35)',
        fontSize: '0.8425rem',
      }}>
      <Filter size={12} style={{ color: '#eab308' }} />
      <span style={{ color: '#eab308', fontWeight: 600 }}>
        {filters.length} filter{filters.length === 1 ? '' : 's'} active:
      </span>
      {filters.map((f) => (
        <span key={f.column} style={chipStyle}>
          <strong>{columnHeading(columns, columnKeyOf, f.column)}</strong>
          <span style={{ marginLeft: 4, color: 'var(--color-text-secondary)' }}>
            {describeFilter(f)}
          </span>
          <button
            onClick={() => onRemove(f.column)}
            style={chipRemoveStyle}
            title="Remove filter"
            aria-label={`Remove filter on ${f.column}`}
          >
            <X size={10} />
          </button>
        </span>
      ))}
      {filters.length > 1 && (
        <button
          onClick={onClearAll}
          style={{
            background: 'transparent', border: 0, color: '#eab308',
            cursor: 'pointer', fontSize: '0.8125rem', textDecoration: 'underline',
            padding: 0, marginLeft: 4,
          }}
        >
          Clear all
        </button>
      )}
    </div>
  )
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 999,
  padding: '2px 6px 2px 10px',
  color: 'var(--color-text)',
}

const chipRemoveStyle: React.CSSProperties = {
  marginLeft: 4,
  width: 16, height: 16,
  borderRadius: '50%',
  background: 'transparent',
  border: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
}
